import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { App } from './app';
import { ErrorBoundary } from './error-boundary';
import { ConnectionRecovery } from '@providers/connection-recovery';
import { themes, ThemeProvider } from '@defa/defa-component-library';
import { THEME_INDEX } from './constants';
import './sentry';
import * as Sentry from '@sentry/react';

// Minimal silent fix for ChunkLoadError
const KEY = 'ChunkReloadedOnce';

function maybeRecoverFromChunkError(err: any, prevent?: () => void) {
    const msg = String(err?.message || err);
    const isChunk =
        err?.name === 'ChunkLoadError' || /Loading chunk/i.test(msg) || /dynamic import/i.test(msg);
    if (!isChunk) return;

    // Don't loop while offline; wait for 'online' to refetch
    if (navigator.onLine === false) return;

    if (!sessionStorage.getItem(KEY)) {
        sessionStorage.setItem(KEY, '1');
        const url = new URL(window.location.href);
        url.searchParams.set('__v', String(Date.now())); // cache-bust
        setTimeout(() => (window.location.href = url.toString()), 50);
        prevent?.();
    }
}

window.addEventListener('unhandledrejection', (e: PromiseRejectionEvent) =>
    maybeRecoverFromChunkError(e.reason, () => e.preventDefault())
);
window.addEventListener('error', (e: ErrorEvent) => maybeRecoverFromChunkError(e.error));

const container = document.getElementById('root');
const root = createRoot(container!, {
    // Callback called when an error is thrown and not caught by an ErrorBoundary.
    onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
        /* eslint-disable no-console */
        console.warn('Uncaught error', error, errorInfo.componentStack);
    }),
    // Callback called when React catches an error in an ErrorBoundary.
    onCaughtError: Sentry.reactErrorHandler(),
    // Callback called when React automatically recovers from errors.
    onRecoverableError: Sentry.reactErrorHandler(),
});

root.render(
    <StrictMode>
        <ThemeProvider theme={themes[THEME_INDEX]}>
            <ErrorBoundary>
                <BrowserRouter>
                    <ConnectionRecovery />
                    <App />
                </BrowserRouter>
            </ErrorBoundary>
        </ThemeProvider>
    </StrictMode>
);

// Clear the one-shot flag once we've mounted successfully (prevents blocking a future recover)
setTimeout(() => sessionStorage.removeItem(KEY), 3000);
